import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors, useField } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { userResponsibilityService } from "../../../shared/api/userResponsibilityService";
import { UserResponsibilityFormValues, UserResponsibilityType } from "../../../shared/types/userResponsibility";
import { UserResponsibilityTypeSelectField } from "./UserResponsibilityTypeSelectField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: UserResponsibilityFormValues) => {
		const errors: FormikErrors<UserResponsibilityFormValues> = {};

		if (!values.type) {
			errors.type = t("Field is required");
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, translation) => {
				const errors: Record<string, any> = {};
				if (!translation.name) {
					errors.name = t("Field is required");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation = { name: "" };

const UserResponsibilityFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const [{ value: type }] = useField<UserResponsibilityType>("type");

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required />
			<I18nOnlyOnMainForm>
				<UserResponsibilityTypeSelectField
					name="type"
					label={t("Type")}
					required
					isParticipant={type === "participant"}
				/>
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const UserResponsibilityCreateDrawer: FC<CreateDrawerFormProps<UserResponsibilityFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();

	const initialValues: UserResponsibilityFormValues = useMemo(
		() => ({
			translations: [{ locale: languageSettings.fallback, name: "" }],
			type: null,
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create user responsibility")}
			validate={validate}
			renderForm={(formProps) => <UserResponsibilityFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

export const UserResponsibilityUpdateDrawer: FC<UpdateDrawerFormProps<UserResponsibilityFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit user responsibility")}
			queryFn={() => userResponsibilityService.show(props.id).then((x) => x.data.data)}
			queryKey={userResponsibilityService.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <UserResponsibilityFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
